import { useEffect, useMemo } from "react";
import { generateHTML, type Extensions } from "@tiptap/core";
import { EditorContent, useEditor as useTipTap } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useValue } from "@tldraw/state-react";
import {
  defaultShapeSchemas,
  toRichText,
  type TLRichText,
  type TLTextShape,
} from "@tldraw/tlschema";
import { useEditor } from "../../hooks";
import type { PlanroomEditor } from "../../editor";
import { Rectangle2d } from "../geometry2d";
import { ShapeUtil, HTMLContainer, type ResizeInfo } from "../shape-util";
import type { CanvasEditor } from "../editor";
import { isEmptyRichText, useFocusWhenMounted } from "./rich-label";

// Text: rich text drawn as HTML, measured for its frame, and edited with TipTap.

export type TextStyleValues = { fontSize: number; lineHeight: number };
const SIZE_MULTIPLIER = { s: 1.125, m: 1.5, l: 2.25, xl: 2.75 } as const;
const PADDING = 4;

export class TextShapeUtil extends ShapeUtil<TLTextShape> {
  static override type = "text";
  static override props = defaultShapeSchemas.text.props;
  /** Rich text extensions used to render and edit text. */
  extensions: Extensions = [StarterKit];

  getDefaultProps(): TLTextShape["props"] {
    return {
      color: "black",
      size: "m",
      w: 8,
      font: "draw",
      textAlign: "start",
      autoSize: true,
      scale: 1,
      richText: toRichText(""),
    };
  }
  /** Font size and line height for a shape; subclasses may override. */
  getTextStyle(shape: TLTextShape): TextStyleValues {
    const theme = this.editor.getCurrentTheme();
    return {
      fontSize: theme.fontSize * SIZE_MULTIPLIER[shape.props.size],
      lineHeight: theme.lineHeight,
    };
  }
  fontFamily(shape: TLTextShape) {
    const fonts = this.editor.getCurrentTheme().fonts as unknown as Record<
      string,
      { fontFamily: string }
    >;
    return fonts[shape.props.font]?.fontFamily ?? "sans-serif";
  }
  html(richText: TLRichText) {
    if (typeof document === "undefined") return "";
    try {
      return generateHTML(richText as never, this.extensions);
    } catch {
      return "";
    }
  }
  /** The unscaled frame size of the text. */
  getMinDimensions(shape: TLTextShape) {
    const { fontSize, lineHeight } = this.getTextStyle(shape);
    if (typeof document === "undefined")
      return {
        width: Math.max(shape.props.w, 16),
        height: fontSize * lineHeight + PADDING * 2,
      };
    const measured = this.editor.textMeasure.measureHtml(
      this.html(shape.props.richText) || "&#8203;",
      {
        fontStyle: "normal",
        fontWeight: "normal",
        fontFamily: this.fontFamily(shape),
        fontSize,
        lineHeight,
        maxWidth: shape.props.autoSize ? null : shape.props.w,
        padding: `${PADDING}px`,
      },
    );
    return {
      width: shape.props.autoSize ? Math.max(16, measured.w) : shape.props.w,
      height: Math.max(fontSize * lineHeight + PADDING * 2, measured.h),
    };
  }
  getGeometry(shape: TLTextShape) {
    const { width, height } = this.getMinDimensions(shape);
    return new Rectangle2d({
      width: width * shape.props.scale,
      height: height * shape.props.scale,
      isFilled: true,
    });
  }
  override getIndicatorPath(shape: TLTextShape) {
    const b = this.getGeometry(shape).bounds;
    return `M0,0H${b.w}V${b.h}H0Z`;
  }
  override canEdit(_shape: TLTextShape) {
    return true;
  }
  override getText(shape: TLTextShape) {
    return this.html(shape.props.richText)
      .replace(/<[^>]+>/g, " ")
      .trim();
  }
  /** Edge drags rewrap the text; scaling a group of shapes scales it. */
  override onResize(shape: TLTextShape, info: ResizeInfo<TLTextShape>) {
    if (info.mode === "scale_shape") {
      const scale = shape.props.scale * Math.abs(info.scaleX);
      return {
        id: shape.id,
        type: shape.type,
        x: info.newPoint.x,
        y: info.newPoint.y,
        props: { scale },
      };
    }
    const width = Math.max(
      16,
      (info.initialBounds.w / shape.props.scale) * Math.abs(info.scaleX),
    );
    return {
      id: shape.id,
      type: shape.type,
      x: info.newPoint.x,
      y: info.newPoint.y,
      props: { w: width, autoSize: false },
    };
  }
  component(shape: TLTextShape) {
    return <TextBody shape={shape} util={this} />;
  }
}

function TextBody({
  shape,
  util,
}: {
  shape: TLTextShape;
  util: TextShapeUtil;
}) {
  const editor = useEditor();
  const editing = useValue(
    "editing text",
    () => editor.getEditingShapeId() === shape.id,
    [editor, shape.id],
  );
  const { fontSize, lineHeight } = util.getTextStyle(shape);
  const theme = editor.getCurrentTheme() as unknown as {
    colors: { light: Record<string, { solid: string }> };
  };
  const style = {
    fontFamily: util.fontFamily(shape),
    fontSize,
    lineHeight,
    color: /^#[a-fA-F0-9]{6}$/.test(String(shape.meta.exactColor))
      ? String(shape.meta.exactColor)
      : (theme.colors.light[shape.props.color]?.solid ?? "#1d1d1d"),
    textAlign: ({ start: "left", middle: "center", end: "right" } as const)[
      shape.props.textAlign
    ],
    padding: PADDING,
    width: shape.props.autoSize ? "max-content" : shape.props.w,
    transform: `scale(${shape.props.scale})`,
    transformOrigin: "0 0",
    ["--canvas-text-scale" as string]: shape.props.scale,
    whiteSpace: "pre-wrap" as const,
    overflowWrap: "break-word" as const,
  };
  const html = useMemo(
    () => util.html(shape.props.richText),
    [util, shape.props.richText],
  );
  return (
    <HTMLContainer
      className="canvas-text"
      style={style}
      data-isediting={editing}
      data-hastext={!isEmptyRichText(shape.props.richText)}
    >
      {editing ? (
        <TextEditor editor={editor} shape={shape} util={util} />
      ) : (
        <div
          className="canvas-text-content"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </HTMLContainer>
  );
}

function TextEditor({
  editor,
  shape,
  util,
}: {
  editor: PlanroomEditor;
  shape: TLTextShape;
  util: TextShapeUtil;
}) {
  const tiptap = useTipTap({
    extensions: util.extensions,
    content: shape.props.richText as never,
    autofocus: "end",
    editorProps: {
      handleKeyDown: (_view, event) => {
        if (event.key === "Escape") {
          editor.cancel();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: rich }) => {
      editor.updateShape({
        id: shape.id,
        type: "text",
        props: { richText: rich.getJSON() as TLRichText },
      });
    },
  });
  useEffect(() => {
    const canvas = editor as unknown as CanvasEditor;
    canvas.setRichTextEditor?.(tiptap);
    return () => canvas.setRichTextEditor?.(null);
  }, [editor, tiptap]);
  useFocusWhenMounted(tiptap);
  return (
    <div data-canvas-interactive onPointerDown={(e) => e.stopPropagation()}>
      <EditorContent editor={tiptap} />
    </div>
  );
}
